import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput2 } from 'components/PlainInput/PlainInput2'
import { SaveChanges } from 'components/SaveChanges'
import { ProductTypes, SettingKey } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Select } from 'stories/components'
import { confirm, removeComma } from 'utils'

import { ILoanGlobalSettings, ILTCFormula, ILTCFormulas } from '../../GlobalSettings'

const defaultFormula: ILTCFormula = {
  loanAmountFactor: 1,
  constructionBudgetFactor: 1,
  interestReserveFactor: 1,
  loanAmountPercentageFactor: 0,
  fixedAmount: 0,
}

const Term = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[13px] whitespace-nowrap">
    {children}
  </span>
)

export const LTCFormulas = () => {
  const [globalSetting, setGlobalSetting] = useState<ILoanGlobalSettings>()
  const [ltcFormulas, setLTCFormulas] = useState<ILTCFormulas[]>([])
  const [isChanged, setChanged] = useState(false)
  const [isLoading, setLoading] = useState(true)

  const productTypeOptions: Record<string, string> = useMemo(
    () => ({
      [ProductTypes.FixAndFlip]: 'Fix & Flip / Fix to Rent',
      [ProductTypes.GroundUpConstruction]: 'Ground Up Construction',
    }),
    [],
  )

  useEffect(() => {
    setLoading(true)
    getAdminConfig(SettingKey.GLOBAL_SETTINGS)
      .then((res) => {
        setGlobalSetting(res)
        setLTCFormulas(res?.ltcFormulas || [])
      })
      .finally(() => setLoading(false))
  }, [])

  const selectedProductTypes = ltcFormulas.map((item) => item.productType)
  const canAdd = ltcFormulas.length < Object.keys(productTypeOptions).length

  const productTypeError = (index: number) => {
    const productType = selectedProductTypes[index]
    if (!productType) return 'Select a product type'
    if (selectedProductTypes.indexOf(productType) !== index) return 'This product type is already configured'
    return ''
  }

  const onChangeProductType = (index: number, value: string) => {
    setLTCFormulas(ltcFormulas.map((item, i) => (i === index ? { ...item, productType: value } : item)))
    setChanged(true)
  }

  const onChangeFormula = (index: number, key: keyof ILTCFormula, value: string) => {
    const next = removeComma(value)
    setLTCFormulas(
      ltcFormulas.map((item, i) =>
        i === index ? { ...item, formula: { ...item.formula, [key]: Number.isFinite(next) ? next : 0 } } : item,
      ),
    )
    setChanged(true)
  }

  const onAdd = () => {
    setLTCFormulas([...ltcFormulas, { productType: '', formula: { ...defaultFormula } }])
    setChanged(true)
  }

  const onRemove = async (index: number) => {
    const { productType } = ltcFormulas[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to delete this LTC formula?
        <br />
        <span className="text-gray-600 text-base">
          Product Type: {productTypeOptions[productType] || 'Not selected'}
        </span>
      </div>
    )
    if (!(await confirm(content))) return

    setLTCFormulas(ltcFormulas.filter((_, i) => i !== index))
    setChanged(true)
  }

  const onSubmit = async () => {
    if (!globalSetting) return
    if (ltcFormulas.some((_, index) => !!productTypeError(index))) return

    const newSetting = cloneDeep(globalSetting)
    newSetting.ltcFormulas = ltcFormulas

    setLoading(true)
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSetting)
    setGlobalSetting(newSetting)
    setLoading(false)
    setChanged(false)
  }

  const renderFactor = (index: number, key: keyof ILTCFormula, value: number, prefix = '', suffix = '') => (
    <span className="inline-flex w-[4.5rem] items-center justify-center rounded border border-dashed border-gray-300 bg-white transition-colors hover:border-shade-blue focus-within:border-shade-blue">
      <PlainInput2
        value={String(value)}
        origin={String(value)}
        content={value.toLocaleString()}
        prefix={prefix}
        suffix={suffix}
        onChange={(newValue: any) => onChangeFormula(index, key, newValue)}
        className="mb-0 text-center"
      />
    </span>
  )

  const renderFormula = (index: number, formula: ILTCFormula) => (
    <div className="overflow-x-auto rounded-md border border-gray-100 bg-gray-50 px-3 py-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Loan to Cost</p>
      <div className="inline-flex flex-col items-center text-sm text-gray-700">
        <div className="flex w-full items-center justify-center gap-x-1.5 gap-y-2 px-3 pb-1.5">
          {renderFactor(index, 'loanAmountFactor', formula.loanAmountFactor)}
          <span>&times;</span>
          <Term>Proposed Loan Amount</Term>
        </div>
        <div className="h-px w-full bg-gray-400" />
        <div className="flex w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-2 px-3 pt-1.5">
          <span>Min(</span>
          <Term>As Is Appraised Value</Term>
          <span>,</span>
          <Term>Property Purchase Price</Term>
          <span>)</span>
          <span>+</span>
          {renderFactor(index, 'constructionBudgetFactor', formula.constructionBudgetFactor)}
          <span>&times;</span>
          <Term>Construction Budget</Term>
          <span>+</span>
          {renderFactor(index, 'interestReserveFactor', formula.interestReserveFactor)}
          <span>&times;</span>
          <Term>Interest Reserve</Term>
          <span>+</span>
          {renderFactor(index, 'loanAmountPercentageFactor', formula.loanAmountPercentageFactor, '', '%')}
          <span>of</span>
          <Term>Proposed Loan Amount</Term>
          <span>+</span>
          {renderFactor(index, 'fixedAmount', formula.fixedAmount, '$')}
        </div>
      </div>
    </div>
  )

  return (
    <div className="relative mt-2">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      <div className="mb-3 flex items-center justify-between border-b px-2 pb-1">
        <span className="text-base font-bold">LTC Formulas</span>
        {canAdd && (
          <div className="flex items-center gap-1 text-sm text-shade-blue hover:cursor-pointer" onClick={onAdd}>
            <PlusIcon className="h-4 w-4" />
            <span>Add LTC Formula</span>
          </div>
        )}
      </div>

      <p className="mb-4 px-2 text-sm text-gray-500">
        Define how the Loan to Cost ratio is calculated for each product type. Click any highlighted number to edit it.
      </p>

      {!isLoading && !ltcFormulas.length && (
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500">
          No LTC formula configured yet.
          <span className="ml-1 text-shade-blue hover:cursor-pointer" onClick={onAdd}>
            Add one
          </span>{' '}
          to get started.
        </div>
      )}

      <div className="flex flex-col gap-4">
        {ltcFormulas.map((item, index) => (
          <div key={index} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="w-full max-w-[22rem]">
                <Select
                  id={`productType-${index}`}
                  title="Product Type"
                  hasDefaultOption
                  options={productTypeOptions}
                  value={item.productType}
                  error={productTypeError(index)}
                  onChange={(value) => onChangeProductType(index, value)}
                />
              </div>
              <span className="btn-icon !text-red-500" onClick={() => onRemove(index)}>
                <TrashIcon className="h-4 w-4" />
              </span>
            </div>

            {renderFormula(index, item.formula)}
          </div>
        ))}
      </div>
    </div>
  )
}
